import { formatMicros, parseDecimalToMicros, type Micros } from "@dbindex/contracts";

/** Convert a decimal string or micros to micros. Invalid input yields null — never NaN. */
export function toMicros(v: string | number | null | undefined): Micros | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isSafeInteger(v) ? v : null;
  try {
    return parseDecimalToMicros(v);
  } catch {
    return null;
  }
}

/**
 * Display money. Uses integer micros end to end; only the final string is formatted.
 * `precision: "auto"` shows 2 decimals for amounts >= $1 and up to 6 for per-query prices.
 */
export function formatMoney(v: string | number | null | undefined, opts: { precision?: "auto" | number; compact?: boolean } = {}): string {
  const m = toMicros(v);
  if (m === null) return "—";
  const abs = Math.abs(m);
  if (opts.compact && abs >= 1_000_000_000) {
    const whole = Math.floor(abs / 1_000_000);
    const s = whole >= 1_000_000 ? `${(whole / 1_000_000).toFixed(1)}M` : `${(whole / 1_000).toFixed(1)}k`;
    return `${m < 0 ? "-" : ""}$${s}`;
  }
  let decimals: number;
  if (typeof opts.precision === "number") decimals = opts.precision;
  else if (abs >= 1_000_000) decimals = 2;
  else if (abs === 0) decimals = 2;
  else {
    // per-query prices: trim trailing zeros but keep at least 4 decimals
    const full = formatMicros(abs, 6);
    const trimmed = full.replace(/0+$/, "");
    const d = trimmed.split(".")[1]?.length ?? 0;
    decimals = Math.max(4, d);
  }
  const s = formatMicros(abs, decimals);
  const [w, f] = s.split(".");
  const wholeGrouped = Number(w).toLocaleString("en-US");
  return `${m < 0 ? "-" : ""}$${wholeGrouped}${f ? "." + f : ""}`;
}

export function microsToDecimalString(m: Micros): string {
  return formatMicros(m, 6);
}

export function formatNumber(n: number | null | undefined, opts: { compact?: boolean; decimals?: number } = {}): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  if (opts.compact) return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n);
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: opts.decimals ?? 0 }).format(n);
}

export function formatPercent(r: number | null | undefined, decimals = 1): string {
  if (r === null || r === undefined || !Number.isFinite(r)) return "—";
  return `${(r * 100).toFixed(decimals)}%`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "—";
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds < 10 ? seconds.toFixed(1).replace(/\.0$/, "") : Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1).replace(/\.0$/, "")}h`;
  return `${(seconds / 86400).toFixed(1).replace(/\.0$/, "")}d`;
}

export function formatBytes(b: number | null | undefined): string {
  if (b === null || b === undefined || !Number.isFinite(b)) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${u[i]}`;
}

export function timeAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, (now - t) / 1000);
  if (s < 5) return "just now";
  return `${formatDuration(s)} ago`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function shortId(id: string | null | undefined): string {
  if (!id) return "—";
  return id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}
